// FILE: src/support/SupportTickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccess } from "../access/AccessContext";
import { auth, db } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  where,
  query,
} from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";

export default function SupportTickets() {
  const navigate = useNavigate();
  // Auth (hydrate properly)
  const [me, setMe] = useState(() => auth.currentUser || null);
  const { readOnly } = useAccess() || { readOnly: false };
  
  // Profile + form fields
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [feature, setFeature] = useState("");
  const [priority, setPriority] = useState("normal");
  const [issue, setIssue] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState(null); // FileList

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { ticket, id }
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);       // modal ticket
  const [listenError, setListenError] = useState("");

  const disableFields = submitting || readOnly;
  
  // Transient hint for locked identity fields
  const [lockHint, setLockHint] = useState(false);
  function showLockHint() {
    setLockHint(true);
    window.clearTimeout(showLockHint._t);
    showLockHint._t = window.setTimeout(() => setLockHint(false), 2000);
  }

  // Track auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setMe(u || null));
    return () => unsub();
  }, []);

  // Load display name/email/phone from Firestore users doc (or Auth fallback)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!me) return;
      const userRef = doc(db, "users", me.uid);
      const snap = await getDoc(userRef).catch(() => null);
      const data = snap?.exists() ? snap.data() : {};

      const fullName =
        data?.profile?.fullName ||
        data?.displayName ||
        data?.name ||
        me.displayName ||
        "";

      const email = data?.email || data?.profile?.email || me.email || "";

      const phone =
        data?.profile?.phone ||
        data?.phone ||
        data?.profile?.phoneNumber ||
        me.phoneNumber ||
        "";

      if (!cancelled) setProfile({ name: fullName, email, phone });
    })();
    return () => { cancelled = true; };
  }, [me]);

  // Features list (includes Contacts & Account Managers)
  const features = useMemo(
    () => [
      "Vaults",
      "Messages",
      "Legal",
      "Personal",
      "Proof of Life",
      "Contacts",
      "Account Managers",
      "Billing / Subscription",
      "Other",
    ],
    []
  );

  function tsMillis(x) {
    if (!x) return 0;
    if (typeof x.toMillis === "function") return x.toMillis();
    if (x.seconds != null) return x.seconds * 1000;
    if (x instanceof Date) return x.getTime();
    return 0;
  }

  function normalizeStatusKey(t) {
    const raw = String(t?.status || "").toLowerCase();
    let k = raw;
    if (k === "live") k = "open";
    if (k === "solved") k = "closed";
    if (k === "pending" || k === "replied") k = "awaiting_customer";
    if (!["open", "awaiting_support", "awaiting_customer", "closed"].includes(k)) k = "open";

    // If older tickets only have open/closed, infer the awaiting state from last reply role.
    if (k === "open") {
      const r = String(t?.lastReplyRole || "").toLowerCase();
      if (r === "staff" || r === "support" || r === "admin") return "awaiting_customer";
      if (r === "user") return "awaiting_support";
    }
    return k;
  }

  function statusLabel(k) {
    if (k === "awaiting_support") return "Awaiting Support";
    if (k === "awaiting_customer") return "Awaiting Customer";
    if (k === "closed") return "Closed";
    return "Open";
  }

  function bestTs(t) {
    return t?.lastReplyAt || t?.lastMessageAt || t?.updatedAt || t?.createdAt || null;
  }

    // 🔁 Live list of MY tickets: listen by userId (primary). Email-only fallback is used
  //    only when we don't have an authenticated user (legacy edge). The previous
  //    behavior always attempted the email query which violated Firestore rules
  //    (`list` requires a `userId` equality filter) and surfaced the "Missing or
  //    insufficient permissions" warning even when the profile was complete.
  useEffect(() => {
    const unsubs = [];
    const byId = new Map();
    setListenError("");

    const applySnap = (snap) => {
      let changed = false;
      snap.forEach((d) => {
        const val = { id: d.id, ...d.data() };
        byId.set(d.id, val); // dedupe by doc id across both listeners
        changed = true;
      });
      if (changed) {
        const rows = Array.from(byId.values()).sort(
          (a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt)
        );
        setList(rows);
      }
    };

    const onErr = (err) => {
      setListenError(err?.message || "Live updates unavailable.");
    };

    try {
      if (me?.uid) {
        const q1 = query(collection(db, "tickets"), where("userId", "==", me.uid));
        unsubs.push(onSnapshot(q1, applySnap, onErr));
           } else if (profile.email) {
        // Legacy fallback: only used when we truly don't have an authenticated UID
        const q2 = query(collection(db, "tickets"), where("email", "==", profile.email));
        unsubs.push(onSnapshot(q2, applySnap, onErr));
      }
    } catch (e) {
      setListenError(e?.message || "Live updates unavailable.");
    }

    return () => unsubs.forEach((u) => u && u());
  }, [me?.uid, profile.email]);

  // Upload attachments to Storage and return metadata (name, url, type, size)
  async function uploadAttachments(uid, ticketId, fileList) {
    if (!fileList || fileList.length === 0) return [];
    const storage = getStorage(); // ← get storage instance here
    const base = `tickets/${uid || "anon"}/${ticketId}/`;
    const uploads = [];
    for (const file of Array.from(fileList)) {
      // eslint fix: dash doesn't need escaping when placed at the end of the class
      const safeName = file.name.replace(/[^\w.-]+/g, "_");
      const ref = storageRef(storage, base + safeName);
      await uploadBytes(ref, file, { contentType: file.type });
      const url = await getDownloadURL(ref);
      uploads.push({
        name: file.name,
        url,
        contentType: file.type || "",
        size: file.size || 0,
      });
    }
    return uploads;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSuccess(null);

    if (readOnly) {
      alert("Support tickets can't be created while you're in read-only mode.");
      return;
    }

    // Enforce required fields (identity is read-only but must exist)
    if (!me) { alert("Please sign in first."); return; }
    if (!profile.name || !profile.email || !profile.phone) {
      alert("Your Name, Email and Phone must be set in your Profile.");
      return;
    }
    if (!feature) { alert("Please select a feature."); return; }
    if (!priority) { alert("Please select a priority."); return; }
    if (!issue.trim()) { alert("Please enter an issue title."); return; }
    if (!description.trim()) { alert("Please describe the issue."); return; }

    setSubmitting(true);

    try {
            // Ensure we send an auth token with the request
      const token = await me.getIdToken();
      
      // 0) If there are files, generate a ticketId and upload attachments first
      let ticketIdForServer = null;
      let attachments = [];

      if (files && files.length) {
        const newDocRef = doc(collection(db, "tickets"));
        ticketIdForServer = newDocRef.id;
        attachments = await uploadAttachments(me.uid, ticketIdForServer, files);
      }

      // 1) Create the ticket via your function (sends email + stores attachments)
      const res = await fetch("/api/support/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: profile.name,
          email: profile.email,
          phone: profile.phone,
          feature,
          priority,
          issue,
          description,
          uid: me?.uid || undefined,
          ticketId: ticketIdForServer || undefined,
          attachments, // URLs + metadata so the email can include links
        }),
      });

      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out?.error || "submit_failed");

      setSuccess({ ticket: out.ticket, id: out.id });

      // Reset problem fields (keep identity locked; clear files)
      setFeature("");
      setPriority("normal");
      setIssue("");
      setDescription("");
      setFiles(null);
      const fileEl = document.getElementById("ticket-files");
      if (fileEl) fileEl.value = "";
    } catch (err) {
      alert(`Submit failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="support-wrap">
      <div className="card">
        <h3 className="card-title">Create a Support Ticket</h3>

        {/* Transient hint for locked fields */}
        {lockHint && (
          <div className="alert info" role="status">
            These identity fields come from your account profile and can’t be edited here.
          </div>
        )}

        {success && (
          <div className="alert success" role="status">
            Ticket <b>#{success.ticket}</b> has been created.
          </div>
        )}
        {listenError && (
          <div className="alert warn" role="status">
            {listenError} Showing what we can.
          </div>
        )}

        {readOnly && (
          <div className="alert warn" role="status">
            You have read-only access. Existing tickets are visible, but new tickets can't be created here.
          </div>
        )}

        <form onSubmit={onSubmit} className="form-grid" noValidate>
          {/* Name 100% */}
          <div className="col-12">
            <label className="label">Your name</label>
            <input
              className="input"
              type="text"
              value={profile.name || ""}
              readOnly
              onFocus={showLockHint}
              onClick={showLockHint}
              title="This comes from your profile and cannot be changed here."
              placeholder="Your full name"
              required
            />
          </div>

          {/* Email 50% / Phone 50% */}
          <div className="col-6">
            <label className="label">Your email</label>
            <input
              className="input"
              type="email"
              value={profile.email || ""}
              readOnly
              onFocus={showLockHint}
              onClick={showLockHint}
              title="This comes from your profile and cannot be changed here."
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="col-6">
            <label className="label">Your phone</label>
            <input
              className="input"
              type="text"
              value={profile.phone || ""}
              readOnly
              onFocus={showLockHint}
              onClick={showLockHint}
              title="This comes from your profile and cannot be changed here."
              placeholder="+353 …"
              required
            />
          </div>

          <div>
            <label className="label">Feature</label>
            <select
              className="input"
              value={feature}
              onChange={(e) => {
                if (readOnly) return;
                setFeature(e.target.value);
              }}
              disabled={disableFields}
              required
            >
              <option value="" disabled>Select a feature</option>
              {features.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Priority</label>
            <select
              className="input"
              value={priority}
              onChange={(e) => {
                if (readOnly) return;
                setPriority(e.target.value);
              }}
              disabled={disableFields}
              required
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="col-12">
            <label className="label">Issue (short title)</label>
            <input
              className="input"
              type="text"
              value={issue}
              onChange={(e) => {
                if (readOnly) return;
                setIssue(e.target.value);
              }}
              disabled={disableFields}
              placeholder="e.g. Can't upload PDF"
              required
            />
          </div>

          <div className="col-12">
            <label className="label">Description</label>
            <textarea
              className="input"
              rows={5}
              value={description}
              onChange={(e) => {
                if (readOnly) return;
                setDescription(e.target.value);
              }}
              disabled={disableFields}
              placeholder="Describe what happened…"
              required
            />
          </div>

          {/* Attach images */}
          <div className="col-12">
            <label className="label">Attachments (images)</label>
            <input
              id="ticket-files"
              className="input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                if (readOnly) return;
                setFiles(e.target.files);
              }}
              disabled={disableFields}
            />
            <div className="hint">Optional: you can attach screenshots/photos.</div>
          </div>

          <div className="actions">
            <button className="btn" disabled={disableFields}>
              {submitting ? "Submitting…" : "Create Ticket"}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="list-head">
          <h3 className="card-title">My Tickets</h3>
        </div>

        {list.length === 0 ? (
          <div className="empty">No tickets yet.</div>
        ) : (
          <div className="tickets">
            {list.map((t) => {
              const k = normalizeStatusKey(t);
              const lastTs = bestTs(t);
              const lastTsText = lastTs?.toDate ? lastTs.toDate().toLocaleString() : "—";
              const lastRole = String(t.lastReplyRole || "").toLowerCase();
              const lastFrom = lastRole === "staff" || lastRole === "support" || lastRole === "admin" ? "Support" : "You";

              return (
              <div key={t.id} className="ticket-row">
                <div className="left">
                  <div className="tk-title">
                    {t.ticketNumber ? `#${t.ticketNumber} — ` : ""}
                    {t.issue || "(no title)"}
                  </div>
                  <div className="tk-sub">
                    <span className={"badge p-" + (t.priority || "normal")}>
                      {t.priority || "normal"}
                    </span>
                    <span className="dot">•</span>
                    <span className="muted">
                      {t.feature || "Unspecified feature"}
                    </span>
                  </div>
                </div>
                <div className="right">
                  <div style={{ textAlign: "right" }}>
                    <span className={"status s-" + k}>
                      {statusLabel(k)}
                    </span>
                    <div className="muted" style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                      Last reply {lastFrom} • {lastTsText}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginRight: 8 }}
                    onClick={() => navigate(`/support/t/${t.id}`)}
                  >
                    Thread
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setView(t)}
                  >
                    View
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* Orange-themed modal */}
      {view && (
        <div className="modal" onClick={() => setView(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="t">
                Ticket {view.ticketNumber ? `#${view.ticketNumber}` : ""}
              </div>
              <button className="x" onClick={() => setView(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="kv">
                <div className="k">Issue</div>
                <div className="v">{view.issue || "-"}</div>
              </div>
              <div className="kv">
                <div className="k">Feature</div>
                <div className="v">{view.feature || "-"}</div>
              </div>
              <div className="kv">
                <div className="k">Priority</div>
                <div className="v">
                  <span className={"badge p-" + (view.priority || "normal")}>
                    {view.priority || "normal"}
                  </span>
                </div>
              </div>
              <div className="kv">
                <div className="k">Status</div>
                <div className="v">
                  <span className={"status s-" + normalizeStatusKey(view)}>
                    {statusLabel(normalizeStatusKey(view))}
                  </span>
                </div>
              </div>

              <div className="kv">
                <div className="k">Last reply</div>
                <div className="v">
                  {(() => {
                    const ts = bestTs(view);
                    const txt = ts?.toDate ? ts.toDate().toLocaleString() : "—";
                    const lr = String(view.lastReplyRole || "").toLowerCase();
                    const from = lr === "staff" || lr === "support" || lr === "admin" ? "Support" : "You";
                    return `${from} · ${txt}`;
                  })()}
                </div>
              </div>
              <div className="kv col">
                <div className="k">Description</div>
                <div className="v">{view.description || "-"}</div>
              </div>

              {/* Show admin notes if any */}
              {view.notes ? (
                <div className="kv col">
                  <div className="k">Notes</div>
                  <div className="v" style={{ whiteSpace: "pre-wrap" }}>{view.notes}</div>
                </div>
              ) : null}

              {/* Attachments (thumbnails) */}
              {Array.isArray(view.attachments) && view.attachments.length > 0 && (
                <div className="kv col">
                  <div className="k">Attachments</div>
                  <div className="v">
                    <div className="att-grid">
                      {view.attachments.map((a, idx) => (
                        <a key={idx} href={a.url} target="_blank" rel="noreferrer" className="att">
                          <img src={a.url} alt={a.name || `attachment-${idx}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setView(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .card{display:block;background:#fff;border:1px solid #ececf5;border-radius:12px;padding:12px;margin:12px 0}
        .card-title{margin:4px 6px 12px 6px;font-weight:800;color:#2a0516}

        .alert{margin:8px 6px 12px 6px;padding:10px 12px;border-radius:10px;font-weight:700}
        .alert.success{background:#E8F5E9;color:#1B5E20;border:1px solid #C8E6C9}
        .alert.warn{background:#FFF8E1;color:#7B5E00;border:1px solid #FFECB3}
        .alert.info{background:#FFF7ED;color:#9A3412;border:1px solid #FED7AA}

        .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        .col-12{grid-column:1 / -1}
        .col-6{grid-column:auto}
        @media(max-width:700px){ .form-grid{grid-template-columns:1fr} .col-6{grid-column:1 / -1} }

        .label{font-size:12px;color:#657899;font-weight:700;margin:0 0 6px 2px}
        .input{width:100%;padding:10px 12px;border:1px solid #d6d6ea;border-radius:10px;background:#fff;color:#2a0516}
        .hint{font-size:12px;color:#657899;margin-top:6px}
        .actions{grid-column:1 / -1;display:flex;justify-content:flex-end;margin-top:4px}
        .btn{background:#EA580C;color:#fff;border:none;border-radius:10px;padding:10px 14px;font-weight:700;cursor:pointer}
        .btn:disabled{opacity:.6;cursor:not-allowed}

        .list-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
        .empty{padding:16px;color:#657899}

        .tickets{display:grid;gap:10px}
        .ticket-row{
          display:grid;grid-template-columns:1fr auto;gap:10px;align-items:center;
          background:#fff;border:1px solid #ececf5;border-radius:12px;padding:10px;
        }
        .tk-title{font-weight:700;color:#2a0516}
        .tk-sub{display:flex;gap:8px;align-items:center;color:#657899}
        .dot{opacity:.6}
        .badge{font-size:11px;font-weight:800;padding:2px 8px;border-radius:999px}
        .badge.p-low{background:#E8F5E9;color:#1B5E20}
        .badge.p-normal{background:#FFEFD5;color:#9A3412;border:1px solid #FED7AA}
        .badge.p-high{background:#FDECEA;color:#B71C1C}
        .status{font-size:12px;font-weight:800;margin-right:8px}
        .status.s-open{color:#0D47A1}
        .status.s-awaiting_support{color:#0D47A1}
        .status.s-awaiting_customer{color:#EF6C00}
        .status.s-pending{color:#EF6C00}
        .status.s-replied{color:#EF6C00}
        .status.s-closed{color:#555}
        .btn-secondary{background:#003353;color:#fff;border:none;border-radius:10px;padding:8px 12px;font-weight:700;cursor:pointer}

        /* Orange-themed modal */
        .modal{position:fixed;inset:0;display:grid;place-items:center;background:rgba(0,0,0,.45);z-index:1000}
        .modal-card{width:min(800px,92vw);background:#fff;border-radius:12px;border:1px solid #ececf5;overflow:hidden}
        .modal-head{display:flex;align-items:center;justify-content:space-between;padding:12px;border-bottom:1px solid #FED7AA;background:#FFF7ED}
        .modal-head .t{font-weight:800;color:#9A3412}
        .modal-body{padding:12px;display:grid;gap:8px}
        .kv{display:grid;grid-template-columns:160px 1fr;gap:8px;align-items:center}
        .kv.col{grid-template-columns:1fr}
        .k{font-size:12px;color:#657899;font-weight:700}
        .v{color:#2a0516}
        .modal-foot{display:flex;justify-content:flex-end;gap:8px;padding:12px;border-top:1px solid #ececf5}

        .x{width:36px;height:36px;border-radius:10px;border:1px solid #FED7AA;background:#fff;cursor:pointer;color:#9A3412}

        /* Attachments */
        .att-grid{display:flex;gap:8px;flex-wrap:wrap}
        .att{display:block;width:96px;height:96px;border:1px solid #ececf5;border-radius:8px;overflow:hidden;background:#fafafa}
        .att img{width:100%;height:100%;object-fit:cover;display:block}

        @media(max-width:720px){ .form-grid{grid-template-columns:1fr} .kv{grid-template-columns:120px 1fr} }
      `}</style>
    </div>
  );
}
